import {
  createContext,
  useContext,
  useEffect,
  type ReactNode,
  type RefObject,
} from 'react';

/**
 * Arrastar o conteúdo com o mouse para rolar de lado — sem ir até a barra.
 *
 * ## Onde vale
 *
 * Pedido para o sinistro em 30/10/2026: o fluxo em linhas, os quadros e as
 * tabelas rolam de lado, e a barra fica lá embaixo, longe de onde o olho está.
 * A `Table` e o `Kanban` perguntam ao `DragScrollProvider` se a tela acendeu;
 * quem desenha a própria região rolável chama o hook direto. Fora do provedor
 * nada muda — as outras telas continuam como eram.
 *
 * ## O que não vira arrasto
 *
 * - **Toque e caneta.** O dedo já rola de lado sozinho; isto é só do mouse.
 * - **Campo de texto e cartão arrastável.** No campo, arrastar seleciona o
 *   texto. No cartão do quadro, o `@dnd-kit` é dono do gesto — ele marca o
 *   cartão com `aria-roledescription`, e é por isso que a guarda olha para ele.
 * - **Clique.** Até 5px de deslocamento é clique, e a linha, a estação ou o
 *   botão abrem como sempre. Passou disso, o clique que o navegador dispara ao
 *   soltar é engolido: soltar o arrasto em cima de uma linha não a abre.
 *
 * Região dentro de região (uma tabela dentro de um quadro) rola só a de dentro:
 * o evento marcado por quem rolou não é tratado de novo por quem o recebe depois.
 */
const DragScrollContext = createContext(false);

export function DragScrollProvider({
  on,
  children,
}: {
  on: boolean;
  children: ReactNode;
}) {
  return <DragScrollContext.Provider value={on}>{children}</DragScrollContext.Provider>;
}

const THRESHOLD = 5;
const NOT_A_HANDLE =
  'input, textarea, select, [contenteditable=""], [contenteditable="true"], [aria-roledescription]';
const handled = new WeakSet<Event>();

export function useDragScroll(ref: RefObject<HTMLElement | null>, on?: boolean) {
  const fromProvider = useContext(DragScrollContext);
  const enabled = on ?? fromProvider;

  useEffect(() => {
    const frame = ref.current;
    if (!enabled || !frame) return;

    const overflows = () => frame.scrollWidth > frame.clientWidth + 1;
    const onEnter = () => {
      frame.style.cursor = overflows() ? 'grab' : '';
    };

    const onDown = (down: PointerEvent) => {
      if (down.pointerType !== 'mouse' || down.button !== 0 || handled.has(down)) return;
      if (!overflows() || (down.target as Element).closest(NOT_A_HANDLE)) return;
      handled.add(down);

      const startX = down.clientX;
      const startLeft = frame.scrollLeft;
      let moved = false;

      const onMove = (move: PointerEvent) => {
        const dx = move.clientX - startX;
        if (!moved) {
          if (Math.abs(dx) < THRESHOLD) return;
          moved = true;
          document.documentElement.classList.add('arrastando-rolagem');
          window.getSelection()?.removeAllRanges();
        }
        frame.scrollLeft = startLeft - dx;
      };
      const swallowClick = (click: MouseEvent) => {
        click.preventDefault();
        click.stopPropagation();
      };
      const onUp = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
        frame.removeEventListener('dragstart', noNativeDrag);
        if (!moved) return;
        document.documentElement.classList.remove('arrastando-rolagem');
        // O clique do soltar chega logo depois do `pointerup`; se não chegar, sai no próximo ciclo.
        window.addEventListener('click', swallowClick, { capture: true, once: true });
        setTimeout(() => window.removeEventListener('click', swallowClick, true), 0);
      };
      // Link e imagem têm o arrasto nativo do navegador, que rouba o ponteiro.
      const noNativeDrag = (e: DragEvent) => e.preventDefault();

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
      frame.addEventListener('dragstart', noNativeDrag);
    };

    onEnter();
    frame.addEventListener('pointerenter', onEnter);
    frame.addEventListener('pointerdown', onDown);
    return () => {
      frame.removeEventListener('pointerenter', onEnter);
      frame.removeEventListener('pointerdown', onDown);
      frame.style.cursor = '';
    };
    /*
     * Sem lista de dependências, como o `useOverflow`: o quadro e a tabela
     * nascem depois do carregamento, e um efeito preso a `[ref]` rodaria uma vez
     * só, com o `ref.current` ainda nulo. O arrasto em curso não se perde ao
     * re-renderizar — quem o acompanha está na `window`, não no quadro.
     */
  });
}
